import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  EventEmitter,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { MiMenuComponent, NotificationService } from '@mitel-internal/primeng-lib';
import { NewConversationDialogComponent } from '../new-conversation-dialog/new-conversation-dialog.component';
import {
  ChatAiConfig,
  ChatComponent,
  ChatConfig,
  ChatInfoComponent,
  ChatSuggestionComponent
} from '@mitel-internal/cloudlink-components-chat';
import { switchMap } from 'rxjs/operators';
import {
  ChatInfoActions,
  ChatInfoButtons
} from '../../../../projects/cloudlink-components-chat/src/lib/enums/chat-info-buttons';
import {
  IChatInfoButtonConfig,
  IChatInfoAboutConfig,
  IChatInfoActionConfig,
  IChatInfoAboutButtonsConfig,
  IChatNotificationSettingsConfig,
  MutedDirect
} from '../../../../projects/cloudlink-components-chat/src/lib/interfaces/chat-info-interfaces';
import { AiConfigService, HANDOFF_MESSAGE } from '../../services/ai-config.service';
import { ConversationListService } from '../../services/conversation-list.service';
import { DevToolsService } from '../../services/dev-tools.service';
import { LocaleService } from '../../services/locale.service';
import { SettingsService } from '../../services/settings.service';
import { ThemeService } from '../../services/theme.service';
import { Subscription } from 'rxjs';
import SwipeListener from 'swipe-listener';
import { getItemsFromCollection } from '@mitel/cloudlink-sdk/utils';
import { AnalyticObject } from './../../../../projects/cloudlink-components-chat/src/lib/interfaces/analytics-interfaces';
import {
  ChatLogEvent,
  ChatLogLevel
} from './../../../../projects/cloudlink-components-chat/src/lib/services/chat-logging.service';

@Component({
  selector: 'app-chat-page',
  templateUrl: './chat-page.component.html',
  styleUrls: ['./chat-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ChatPageComponent implements OnInit, AfterViewInit {
  @Output() loadedEvent = new EventEmitter();

  @ViewChild('michat')
  miChat: ChatComponent;

  @ViewChild('chatSuggestion')
  chatSuggestion: ChatSuggestionComponent;

  @ViewChild('miChatInfo')
  miChatInfo: ChatInfoComponent;

  // mi-round-button is a component, not a plain <button> - read its host element instead of
  // the component instance so it can be used as mi-menu's positioning anchor.
  @ViewChild('addConversationBtn', { read: ElementRef })
  private addConversationBtnRef: ElementRef<HTMLElement>;

  @ViewChild('addConversationMenu')
  private addConversationMenu: MiMenuComponent;

  // SMS is intentionally excluded per design.
  addConversationMenuItems: MenuItem[] = [
    { id: 'add-chat', label: 'Chat', command: () => this.openNewConversationDialog(false) },
    { id: 'add-stream', label: 'Stream', command: () => this.openNewConversationDialog(true) }
  ];

  openAddConversationMenu(): void {
    this.addConversationMenu.toggle(this.addConversationBtnRef.nativeElement);
  }

  openNewConversationDialog(isStream: boolean): void {
    this.dialogService
      .open(NewConversationDialogComponent, {
        header: isStream ? 'New Stream' : 'New Chat',
        width: '30rem',
        height: '25rem',
        data: { isStream }
      })
      .onClose.subscribe((conversationId?: string) => {
        if (conversationId) {
          this.router.navigate(['/messages', conversationId], { queryParamsHandling: 'preserve' });
        }
      });
  }

  conversationId: string;
  config: ChatConfig;
  chatInfoConfig: any;
  showingInfo = false;
  isMobile: boolean;
  offlineConversationId = '';
  useStubSuggestions = true; // Enable stub data for testing

  // For manual aiMode verification against a locally-running MAIS instance (S9). authToken is
  // the Generated API Key from that instance's Settings > API Keys, not a user session JWT -
  // aiMode talks to MAIS's public /v1 API exclusively, a separate auth mechanism from the rest
  // of this demo app's CloudLink-session-based fields above.
  private readonly AI_MODE_CONVERSATION_ID = 'ai-mode-demo';

  // Derived from conversationId rather than a separate mutable flag, so navigating to a regular
  // conversation (e.g. the sidebar's route-params subscription setting conversationId directly)
  // can't leave aiMode stuck true for a real CloudLink conversation - AI only ever applies to
  // its own dedicated conversationId.
  get aiMode(): boolean {
    return this.conversationId === this.AI_MODE_CONVERSATION_ID;
  }

  // Shared with the Options page, which is where the user now edits these - see
  // AiConfigService for the sessionStorage persistence.
  get aiConfig(): ChatAiConfig {
    return this.aiConfigService.config;
  }

  // Mirrors the gate ChatComponent.maybeInitializeAiConversation() uses to silently no-op on an
  // incomplete config - surfaced here instead so the demo app tells the user why aiMode is inert.
  get aiConfigIncomplete(): boolean {
    return this.aiMode && (!this.aiConfig?.authToken || !this.aiConfig?.workspaceSlug);
  }

  // When true (set via the ?embed query param, e.g. for iframe embedding), the page renders only
  // <mi-chat> - no mi-two-pane-layout chrome, header new-conversation button, or sidebar. Read
  // once from the route snapshot in ngOnInit rather than exposed as a live-subscribed getter,
  // since nothing in this app changes it after initial load.
  isEmbedded = false;

  settingsSubscription: Subscription;
  aiConfigSubscription: Subscription;
  newAiChatSubscription: Subscription;

  infoAbout: IChatInfoAboutConfig[] = [
    {
      title: 'Mitel One R&D',
      description: 'Slaying those​ ​🐛 reports!'
    },
    {
      title: 'Description',
      description:
        'Vestibulum rutrum quam vitae fringilla tincidunt. Suspendisse nec tortor urna.\n\nUt laoreet sodales nisi, quis iaculis nulla iaculis vitae. Donec sagittis faucibus lacus eget blandit.'
    },
    {
      title: 'Started',
      description: 'May 13, 2021, 9:40 AM'
    },
    {
      title: 'Started by',
      description: 'You'
    }
  ];

  infoAboutButtonsDefault: IChatInfoAboutButtonsConfig[] = [
    {
      tooltip: 'Video call',
      key: 'video',
      icon: 'mi2-video'
    },
    {
      tooltip: 'Invite to stream',
      key: 'invite',
      icon: 'mi2-add-contact'
    },
    {
      tooltip: 'Search',
      key: 'search',
      icon: 'mi2-search'
    },
    {
      tooltip: 'More',
      key: 'more',
      icon: 'mi2-more-vertical'
    }
  ];
  infoAboutButtons: IChatInfoAboutButtonsConfig[];

  infoButtonsDefault: IChatInfoButtonConfig[] = [
    {
      type: ChatInfoButtons.PARTICIPANTS,
      //imgSrc: 'assets/images/contacts.svg',
      icon: 'mi2-conference-group'
    },
    {
      type: ChatInfoButtons.ALL_ATTACHMENTS,
      //imgSrc: 'assets/images/attachment.svg'
      icon: 'mi2-attachment'
    },
    {
      title: 'Meetings',
      //imgSrc: 'assets/images/meetings.svg',
      icon: 'mi2-meetings',
      type: ChatInfoButtons.GENERIC
    },
    {
      title: 'Tasks',
      //imgSrc: 'assets/images/tasks.svg',
      icon: 'mi2-marquee-widget',
      type: ChatInfoButtons.GENERIC
    },
    {
      type: ChatInfoButtons.NOTIFICATIONS,
      //imgSrc: 'assets/images/settings.svg',
      icon: 'mi2-notification'
    }
  ];
  infoButtons: IChatInfoButtonConfig[] = [];

  infoActionsDefault: IChatInfoActionConfig[] = [
    {
      title: 'Add to home',
      key: 'home',
      color: '#11284c',
      //imgSrc: 'assets/images/home.svg',
      icon: 'mi2-home'
    },
    {
      title: 'Invite to stream',
      key: ChatInfoActions.INVITE,
      color: '#0073d0',
      //imgSrc: 'assets/images/contact-add.svg',
      icon: 'mi2-add-contact'
    },
    {
      title: 'Leave stream',
      key: ChatInfoActions.LEAVE,
      color: '#CD2B26',
      //imgSrc: 'assets/images/leave.svg',
      icon: 'mi2-remove-contact'
    },
    {
      title: 'Hide Direct Message',
      key: ChatInfoActions.HIDE,
      color: '#CD2B26',
      //imgSrc: 'assets/images/leave.svg',
      icon: 'mi2-remove-contact'
    }
  ];

  infoActions: IChatInfoActionConfig[] = [];

  notificationSettings: IChatNotificationSettingsConfig = {
    'optedInStreams': ['5d45515d-504b-4858-8587-aaa41396e8a7', 'ffe48753-3dd4-4f7d-9991-615a8918b7f8'],
    'mutedDirects': [
      {
        conversationId: '71a1b7e98456ee85f0e24cf0a1941884',
        muteUntil: new Date(new Date('Fri Dec 03 2021 11:44:15 GMT-0500 (Eastern Standard Time)').getTime() + 1 * 60000)
      }
    ]
  };

  constructor(
    private route: ActivatedRoute,
    private settingsService: SettingsService,
    public conversationListService: ConversationListService,
    public router: Router,
    public themeService: ThemeService,
    public localeService: LocaleService,
    public devToolsService: DevToolsService,
    private aiConfigService: AiConfigService,
    private dialogService: DialogService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef
  ) {
    this.settingsSubscription = this.settingsService.getSettings().subscribe(settings => {
      this.config = settings ? settings : {};
      this.chatInfoConfig = {};
      this.addAdditionalInfoConfig();
      this.addAdditionalConfig();
      this.cdr.markForCheck();
    });
    this.aiConfigSubscription = this.aiConfigService.configChanged$.subscribe(() => this.cdr.markForCheck());
    this.newAiChatSubscription = this.aiConfigService.newChatRequested$.subscribe(() => this.newAiChat());
    // setTimeout(() => {
    //     this.testListDb();
    // }, 10000)

    //this.customTheme = { '--background-mine': 'linear-gradient(86.66deg, #00A550 0.4%, #007F3D 100%)' };
    this.infoAboutButtons = this.infoAboutButtonsDefault;
    this.infoButtons = this.infoButtonsDefault;
    this.infoActions = this.infoActionsDefault;
  }

  ngOnInit() {
    const embedParam = this.route.snapshot.queryParamMap.get('embed');
    this.isEmbedded = embedParam !== null && embedParam !== 'false';

    this.route.params
      .pipe(
        switchMap((params: Params) => {
          return [params];
        })
      )
      .subscribe(async params => {
        // parse params and setup view
        if (params['id']) {
          this.conversationId = params['id'];
        }

        if (this.conversationId == null) {
          this.conversationId = '';
        }

        this.config = JSON.parse(localStorage.getItem('chatPageOptions'));
        this.chatInfoConfig = {};
        if (!this.config) {
          this.config = {
            showHeader: true,
            headerFontSize: 18,
            headerHeight: 57,
            useDocumentVisibleForUnread: false
            //hideMessageOptionsInfo: true,
            //showMessageOptionsEdit: false,
            //showMessageOptionsDelete: false
          };
        }
        this.addAdditionalConfig();
        this.addAdditionalInfoConfig();

        if (this.conversationId === '8ae4c542-81fe-4c24-b4c4-3c04933cf7ea') {
          console.log('ChatPageComponent - use JSON as messages source', this.conversationId);
          this.addJsonAsMessageSource();
        } else {
          this.infoAboutButtons = this.infoAboutButtonsDefault;
          this.infoButtons = this.infoButtonsDefault;
          this.infoActions = this.infoActionsDefault;
          this.chatInfoConfig.addPeopleButton = true;
        }
        this.cdr.markForCheck();
      });
  }

  ngAfterViewInit() {
    this.setUpSidebarShowHide();
  }

  addAdditionalConfig() {
    // this.config.userReplacements = {
    //     'kevin.lee@mitel.com': null,
    //     '152a9149-8738-4455-85f8-f615e351d8fa': {
    //         displayName: "Test User"
    //     }
    // }
    //this.config.showShareVideo = true;
    //this.config.indexDbConversationListMax = 5;
    //this.config.allowedPlatformSystemMessages = ['micc-participant-removed'];
    this.config.showNamePointer = true;
    this.config.hideReadIndicators = false;
    this.config.saveInput = true;
    this.config.reverseNames = false;
    this.config.reverseMentionSearchNames = false;
    this.config.enableIndexDb = true;
    this.config.indexDbConversationLimit = 5;
    this.config.indexDbAddNewConversations = true;
    this.config.autoJoinStream = true;
    this.config.autoAcceptInvite = true;
    // Only takes effect once a Microsoft Graph token is set from the dev tools menu
    this.config.enableOneDriveLargeFiles = true;
    this.config.focusOnLoad = false;
    this.config.showMessageOptionsEdit =
      this.config.showMessageOptionsEdit == undefined ? true : this.config.showMessageOptionsEdit;
    this.config.showMessageOptionsDelete =
      this.config.showMessageOptionsDelete == undefined ? true : this.config.showMessageOptionsDelete;
    this.config.showMessageOptionsSelect =
      this.config.showMessageOptionsSelect == undefined ? true : this.config.showMessageOptionsSelect;
    // this.config.smsMode = true;
    this.config.logMessageEvents = true;
    // this.config.showMessageOptionsEdit = true;
    // this.config.showMessageOptionsDelete = true;
    // this.config.enableDebugLogs = true; // deprecated, use logLevel: 'debug'
    // this.config.disableLogs = true; // deprecated, use logLevel: 'error'
    // Logging can be tested via query params, e.g. ?logLevel=error&consoleOutput=false
    // consoleOutput=false simulates a mobile webview: the library stays silent and the host forwards onInfoLog/onErrorLog
    const logLevelParam = this.route.snapshot.queryParamMap.get('logLevel') as ChatLogLevel;
    if (logLevelParam) {
      this.config.logLevel = logLevelParam;
    }
    const consoleOutputParam = this.route.snapshot.queryParamMap.get('consoleOutput');
    if (consoleOutputParam !== null) {
      this.config.consoleOutput = consoleOutputParam !== 'false';
    }
    // this.config.enableTwemoji = true;
    this.config.retryFetchOnEmpty = true;
    this.config.useDocumentVisibleForUnread = false;
    this.config.showHostErrors = true;
    this.config.headerTitle = 'Chat Sample';
    this.config.headerShowSearch = true;
    this.config.headerShowBackButton = true;
    this.config.headerButtons = [
      {
        id: 'video',
        tooltip: 'Video meet',
        icon: 'mi2-video',
        disabled: true
      },
      {
        id: 'meeting',
        tooltip: 'Create meeting',
        icon: 'mi2-set-calendar'
      },
      {
        id: 'info',
        tooltip: 'Information',
        icon: 'mi2-info',
        disabled: false
      }
    ];
    //this.config.hideEmojis = true;
    //this.config.hideAddFiles = true;
    //this.config.hideShareAudio = true;
    //this.config.hideShareLocation = true;
    //this.config.usePrimaryGreen = true;
  }

  addAdditionalInfoConfig() {
    this.chatInfoConfig.showAttachmentOptionsDelete = true;
    this.chatInfoConfig.showAttachmentOptionsPreview = true;
    this.chatInfoConfig.showParticipantPointer = true;
    this.chatInfoConfig.addPeopleButton = true;
    this.chatInfoConfig.maxNotificationSettings = 10;
    this.chatInfoConfig.activatePhoneClick = true;
    this.chatInfoConfig.activateEmailClick = true;
    this.chatInfoConfig.singleParticipantReason = 'Slaying those​ ​🐛 reports!';
    this.chatInfoConfig.isOneAppSettingsError = false;
    this.chatInfoConfig.editStreamButton = true;
    this.chatInfoConfig.titleHeight = 57;
    this.chatInfoConfig.titleFontSize = 18;
  }

  addJsonAsMessageSource() {
    this.config.headerTitle = 'Chat Sample - JSON Messages As Source';
    this.config.showHeader = true;
    this.config.headerButtons = [
      {
        id: 'info',
        tooltip: 'Information',
        icon: 'mi2-info',
        disabled: false
      }
    ];
    this.config.headerShowSearch = false;
    this.config.hideMessageOptions = true; // Hide message options for JSON messages
    this.config.jsonMessagesAsSource = true;
    /*this.config.jsonMessagesPayload = {
      'conversationId': 'a5f5527c-e3bd-4628-951f-774932ed8ed4',
      'accountId': '66d43e89-b3d2-477e-b88e-a245c4657137',
      'messages': [
        {
          'accountId': '66d43e89-b3d2-477e-b88e-a245c4657137',
          'conversationId': 'a5f5527c-e3bd-4628-951f-774932ed8ed4',
          'messageId': '464a9d12-5840-4f08-8b6f-259e937708d1',
          'contentType': 'text/plain',
          'createdOn': '2025-08-01T12:56:18.189Z',
          'createdBy': '900c8c34-17e8-4c7d-a233-428f05872208',
          'body': 'no thanks',
          'modifiedOn': '2025-08-01T12:56:18.189Z'
        },
        {
          'accountId': '66d43e89-b3d2-477e-b88e-a245c4657137',
          'conversationId': 'a5f5527c-e3bd-4628-951f-774932ed8ed4',
          'messageId': '37008513-8ff3-456b-8d2d-3e82223dba24',
          'contentType': 'text/plain',
          'createdOn': '2025-08-01T12:56:05.739Z',
          'createdBy': '137c062e-327a-4c49-8d5d-efb26cdb7e74',
          'body': 'Our gym is open 24 hours a day. Anything else I can help you with?',
          'modifiedOn': '2025-08-01T12:56:05.739Z'
        },
        {
          'accountId': '66d43e89-b3d2-477e-b88e-a245c4657137',
          'conversationId': 'a5f5527c-e3bd-4628-951f-774932ed8ed4',
          'messageId': 'c3a703f4-3875-4840-ba13-40192efe6dd7',
          'contentType': 'text/plain',
          'createdOn': '2025-08-01T12:55:58.847Z',
          'createdBy': '900c8c34-17e8-4c7d-a233-428f05872208',
          'body': 'what time is the gym open at',
          'modifiedOn': '2025-08-01T12:55:58.847Z'
        },
        {
          'accountId': '66d43e89-b3d2-477e-b88e-a245c4657137',
          'conversationId': 'a5f5527c-e3bd-4628-951f-774932ed8ed4',
          'messageId': '50777fe2-e9f8-46b6-b213-0e9ae432e751',
          'contentType': 'text/plain',
          'createdOn': '2025-08-01T12:55:45.171Z',
          'createdBy': '137c062e-327a-4c49-8d5d-efb26cdb7e74',
          'body': 'Thank you for calling, what can I help you with today?',
          'modifiedOn': '2025-08-01T12:55:45.171Z'
        },
        {
          'messageId': '1e4115f8-2714-433d-93e7-b29b6404efbb',
          'conversationId': 'a5f5527c-e3bd-4628-951f-774932ed8ed4',
          'accountId': '66d43e89-b3d2-477e-b88e-a245c4657137',
          'contentType': 'application/vnd.mitel.attachment',
          'body': 'sample.png',
          'createdBy': '7341bd4b-facb-40d9-a9e2-b2e4b8fb2fea',
          'createdOn': '2026-02-27T18:17:03.292Z',
          'modifiedBy': '7341bd4b-facb-40d9-a9e2-b2e4b8fb2fea',
          'modifiedOn': '2026-02-27T18:17:03.292Z',
          'fallbackText':
            '<a href="https://devopsmitel-my.sharepoint.com/personal/justin_ghantous_cloudlinkdev_com/Documents/CangsuFolder/sample.png"  target="_blank">Download PNG File</a>',
          'tag': '{"attachmentSize":"13 Bytes"}'
        }
      ],
      'participants': [
        {
          '_links': {
            'self':
              '/2017-09-01/conversations/a5f5527c-e3bd-4628-951f-774932ed8ed4/participants/137c062e-327a-4c49-8d5d-efb26cdb7e74',
            'createdBy': 'https://admin.us.dev.api.mitel.io/2017-09-01/users/9c82d10c-aa4f-4b19-a678-81e04e44f98a',
            'modifiedBy': 'https://admin.us.dev.api.mitel.io/2017-09-01/users/9c82d10c-aa4f-4b19-a678-81e04e44f98a'
          },
          'userId': '137c062e-327a-4c49-8d5d-efb26cdb7e74',
          'conversationId': 'a5f5527c-e3bd-4628-951f-774932ed8ed4',
          'accountId': '66d43e89-b3d2-477e-b88e-a245c4657137',
          'status': 'ACCEPTED',
          'createdBy': '9c82d10c-aa4f-4b19-a678-81e04e44f98a',
          'createdOn': '2025-08-01T12:55:43.368Z',
          'modifiedBy': '9c82d10c-aa4f-4b19-a678-81e04e44f98a',
          'modifiedOn': '2025-08-01T12:55:43.368Z',
          'participantType': 'USER',
          'conversationVisibility': 'VISIBLE',
          '_embedded': {
            'users': {
              'createdBy': 'admin admin',
              'modifiedBy': 'admin admin',
              'name': 'Mitel Assistant'
            }
          }
        },
        {
          '_links': {
            'self':
              '/2017-09-01/conversations/a5f5527c-e3bd-4628-951f-774932ed8ed4/participants/900c8c34-17e8-4c7d-a233-428f05872208',
            'createdBy': 'https://admin.us.dev.api.mitel.io/2017-09-01/users/9c82d10c-aa4f-4b19-a678-81e04e44f98a',
            'modifiedBy': 'https://admin.us.dev.api.mitel.io/2017-09-01/users/9c82d10c-aa4f-4b19-a678-81e04e44f98a'
          },
          'userId': '900c8c34-17e8-4c7d-a233-428f05872208',
          'conversationId': 'a5f5527c-e3bd-4628-951f-774932ed8ed4',
          'accountId': '66d43e89-b3d2-477e-b88e-a245c4657137',
          'status': 'ACCEPTED',
          'createdBy': '9c82d10c-aa4f-4b19-a678-81e04e44f98a',
          'createdOn': '2025-08-01T12:55:44.254Z',
          'modifiedBy': '9c82d10c-aa4f-4b19-a678-81e04e44f98a',
          'modifiedOn': '2025-08-01T12:55:44.254Z',
          'participantType': 'USER',
          'conversationVisibility': 'VISIBLE',
          '_embedded': {
            'users': {
              'createdBy': 'admin admin',
              'modifiedBy': 'admin admin',
              'name': 'Justin Ghantous'
            }
          }
        },
        {
          '_links': {
            'self':
              '/2017-09-01/conversations/a5f5527c-e3bd-4628-951f-774932ed8ed4/participants/9c82d10c-aa4f-4b19-a678-81e04e44f98a',
            'createdBy': 'https://admin.us.dev.api.mitel.io/2017-09-01/users/9c82d10c-aa4f-4b19-a678-81e04e44f98a',
            'modifiedBy': 'https://admin.us.dev.api.mitel.io/2017-09-01/users/9c82d10c-aa4f-4b19-a678-81e04e44f98a',
            'name': 'https://admin.us.dev.api.mitel.io/2017-09-01/users/9c82d10c-aa4f-4b19-a678-81e04e44f98a'
          },
          'userId': '9c82d10c-aa4f-4b19-a678-81e04e44f98a',
          'conversationId': 'a5f5527c-e3bd-4628-951f-774932ed8ed4',
          'accountId': '66d43e89-b3d2-477e-b88e-a245c4657137',
          'status': 'ACCEPTED',
          'createdBy': '9c82d10c-aa4f-4b19-a678-81e04e44f98a',
          'createdOn': '2025-08-01T12:55:42.311Z',
          'modifiedBy': '9c82d10c-aa4f-4b19-a678-81e04e44f98a',
          'modifiedOn': '2025-08-01T12:55:42.311Z',
          'participantType': 'USER',
          'conversationVisibility': 'VISIBLE',
          '_embedded': {
            'users': {
              'createdBy': 'admin admin',
              'modifiedBy': 'admin admin',
              'name': 'admin admin'
            }
          }
        }
      ]
    };*/

    this.config.jsonMessagesPayload = {
      'conversationId': '75222e58-4bb3-43fd-8ab1-7fbf1feaacf8',
      'accountId': 'e85ea4ce-f943-4a98-8d77-201dd54115d8',
      'messages': [
        {
          'accountId': 'e85ea4ce-f943-4a98-8d77-201dd54115d8',
          'conversationId': '75222e58-4bb3-43fd-8ab1-7fbf1feaacf8',
          'messageId': 'ecc94be5-0ff1-4132-846c-d7e83e53df36',
          'contentType': 'application/vnd.mitel.hidden',
          'createdOn': '2026-03-19T20:37:20.121Z',
          'createdBy': '8zd7gkc15h02ry3jvelf',
          'body': '4abf0cf2-e516-4656-a578-4480c6ba521f just left',
          'modifiedOn': '2026-03-19T20:37:20.121Z'
        },
        {
          'accountId': 'e85ea4ce-f943-4a98-8d77-201dd54115d8',
          'conversationId': '75222e58-4bb3-43fd-8ab1-7fbf1feaacf8',
          'messageId': '6820f39c-5a4c-4d27-a88b-9ae10c2f0c83',
          'contentType': 'application/vnd.mitel.system-message',
          'createdOn': '2026-03-19T20:37:18.187Z',
          'createdBy': '8zd7gkc15h02ry3jvelf',
          'body': '{"lookupCode":"micc-session-complete","lookupText":"Session complete","data":null}',
          'fallbackText': 'Session complete',
          'modifiedOn': '2026-03-19T20:37:18.187Z'
        },
        {
          'accountId': 'e85ea4ce-f943-4a98-8d77-201dd54115d8',
          'conversationId': '75222e58-4bb3-43fd-8ab1-7fbf1feaacf8',
          'messageId': '18779fc2-1e99-4766-8145-eadf9b3bdb5e',
          'contentType': 'application/vnd.mitel.hidden',
          'createdOn': '2026-03-19T20:35:11.472Z',
          'createdBy': '8zd7gkc15h02ry3jvelf',
          'body': '4abf0cf2-e516-4656-a578-4480c6ba521f just joined',
          'modifiedOn': '2026-03-19T20:35:11.472Z'
        },
        {
          'accountId': 'e85ea4ce-f943-4a98-8d77-201dd54115d8',
          'conversationId': '75222e58-4bb3-43fd-8ab1-7fbf1feaacf8',
          'messageId': '3fb591eb-8555-4e7b-bdf7-84d26e8e3804',
          'contentType': 'application/vnd.mitel.system-message',
          'createdOn': '2026-03-19T20:35:09.321Z',
          'createdBy': '8zd7gkc15h02ry3jvelf',
          'body':
            '{"lookupCode":"micc-participant-added","lookupText":"{0} just joined","data":["@848a05f3-c9f7-420d-8f0e-6ef60de4fd7e"]}',
          'fallbackText': 'David just joined',
          'modifiedOn': '2026-03-19T20:35:09.321Z'
        },
        {
          'accountId': 'e85ea4ce-f943-4a98-8d77-201dd54115d8',
          'conversationId': '75222e58-4bb3-43fd-8ab1-7fbf1feaacf8',
          'messageId': 'd23107f0-1bb5-41f2-9579-6e9d3e4aaa58',
          'contentType': 'application/vnd.mitel.attachment',
          'createdOn': '2026-03-19T20:35:07.618Z',
          'createdBy': 'whatsapp:+19496836313',
          'body': 'MEb02b735226dfbb5bad76c6124fc97588.jpeg',
          'fallbackText':
            '<a href="https://devopsmitel-my.sharepoint.com/:i:/g/personal/andrew_demski1_cloudlinkdev_com/IQAYHPcBnATHSZhcNDGBBYyuAeujmgAJE12NON7Jx1E_Bk0">MEb02b735226dfbb5bad76c6124fc97588.jpeg</a>',
          'modifiedOn': '2026-03-19T20:35:07.618Z'
        },
        {
          'accountId': 'e85ea4ce-f943-4a98-8d77-201dd54115d8',
          'conversationId': '75222e58-4bb3-43fd-8ab1-7fbf1feaacf8',
          'messageId': '5738efc7-a89b-4c4e-8a89-9275c6fca359',
          'contentType': 'text/plain',
          'createdOn': '2026-03-19T20:35:04.174Z',
          'createdBy': '7665e67e-b578-44a2-9afd-e358f070abe9',
          'body': 'One moment while we check for an available agent.',
          'modifiedOn': '2026-03-19T20:35:04.174Z'
        },
        {
          'accountId': 'e85ea4ce-f943-4a98-8d77-201dd54115d8',
          'conversationId': '75222e58-4bb3-43fd-8ab1-7fbf1feaacf8',
          'messageId': '6a0794cb-dbcb-4023-aadc-258c0d9279e6',
          'contentType': 'text/html',
          'createdOn': '2026-03-19T20:34:55.316Z',
          'createdBy': 'whatsapp:+19496836313',
          'body': 'Hello',
          'modifiedOn': '2026-03-19T20:34:55.316Z'
        }
      ],
      'participants': [
        {
          '_links': {
            'self':
              '/2017-09-01/conversations/75222e58-4bb3-43fd-8ab1-7fbf1feaacf8/participants/7665e67e-b578-44a2-9afd-e358f070abe9'
          },
          'userId': '7665e67e-b578-44a2-9afd-e358f070abe9',
          'conversationId': '75222e58-4bb3-43fd-8ab1-7fbf1feaacf8',
          'accountId': 'e85ea4ce-f943-4a98-8d77-201dd54115d8',
          'status': 'ACCEPTED',
          'createdBy': '8zd7gkc15h02ry3jvelf',
          'createdOn': '2026-03-19T20:35:03.766Z',
          'modifiedBy': '8zd7gkc15h02ry3jvelf',
          'modifiedOn': '2026-03-19T20:35:03.766Z',
          'participantType': 'USER',
          'conversationVisibility': 'VISIBLE',
          '_embedded': {
            'users': {
              'name': 'ChatQueueUsername'
            }
          }
        },
        {
          '_links': {
            'self':
              '/2017-09-01/conversations/75222e58-4bb3-43fd-8ab1-7fbf1feaacf8/participants/848a05f3-c9f7-420d-8f0e-6ef60de4fd7e',
            'modifiedBy': 'https://admin.us.dev.api.mitel.io/2017-09-01/users/848a05f3-c9f7-420d-8f0e-6ef60de4fd7e',
            'name': 'https://admin.us.dev.api.mitel.io/2017-09-01/users/848a05f3-c9f7-420d-8f0e-6ef60de4fd7e'
          },
          'userId': '848a05f3-c9f7-420d-8f0e-6ef60de4fd7e',
          'conversationId': '75222e58-4bb3-43fd-8ab1-7fbf1feaacf8',
          'accountId': 'e85ea4ce-f943-4a98-8d77-201dd54115d8',
          'status': 'ACCEPTED',
          'lastReadMessageDate': '2026-03-19T20:35:09.321Z',
          'createdBy': '8zd7gkc15h02ry3jvelf',
          'createdOn': '2026-03-19T20:35:06.835Z',
          'modifiedBy': '848a05f3-c9f7-420d-8f0e-6ef60de4fd7e',
          'modifiedOn': '2026-03-19T20:35:19.060Z',
          'participantType': 'USER',
          'conversationVisibility': 'VISIBLE',
          '_embedded': {
            'users': {
              'modifiedBy': 'David So',
              'name': 'David So'
            }
          }
        },
        {
          '_links': {
            'self': '/2017-09-01/conversations/75222e58-4bb3-43fd-8ab1-7fbf1feaacf8/participants/8zd7gkc15h02ry3jvelf'
          },
          'userId': '8zd7gkc15h02ry3jvelf',
          'conversationId': '75222e58-4bb3-43fd-8ab1-7fbf1feaacf8',
          'accountId': 'e85ea4ce-f943-4a98-8d77-201dd54115d8',
          'status': 'ACCEPTED',
          'createdBy': '8zd7gkc15h02ry3jvelf',
          'createdOn': '2026-03-19T20:34:50.647Z',
          'modifiedBy': '8zd7gkc15h02ry3jvelf',
          'modifiedOn': '2026-03-19T20:34:50.647Z',
          'participantType': 'USER',
          'conversationVisibility': 'VISIBLE',
          '_embedded': {
            'users': {
              'name': 'MiCC Business Cloudlink Proxy Client - MTL-74DVZC3'
            }
          }
        },
        {
          '_links': {
            'self': '/2017-09-01/conversations/75222e58-4bb3-43fd-8ab1-7fbf1feaacf8/participants/whatsapp:+19496836313'
          },
          'userId': 'whatsapp:+19496836313',
          'conversationId': '75222e58-4bb3-43fd-8ab1-7fbf1feaacf8',
          'accountId': 'e85ea4ce-f943-4a98-8d77-201dd54115d8',
          'status': 'ACCEPTED',
          'createdBy': '8zd7gkc15h02ry3jvelf',
          'createdOn': '2026-03-19T20:34:52.109Z',
          'modifiedBy': '8zd7gkc15h02ry3jvelf',
          'modifiedOn': '2026-03-19T20:34:52.109Z',
          'participantType': 'WORKFLOW',
          'conversationVisibility': 'VISIBLE',
          'endpoint':
            'https://workflow.us.dev.api.mitel.io/2017-09-01/activities/global_c7235677-5e16-40f3-a38c-e3469719b04c/workers',
          '_embedded': {
            'users': {}
          },
          'name': 'not found'
        }
      ]
    };

    // required for Chat Info component to work with JSON messages
    this.chatInfoConfig.getParticipantsFromChatComponent = true;
    this.chatInfoConfig.jsonMessagesAsSource = true;
    this.chatInfoConfig.addPeopleButton = false; // hide add people button in ready-only view if necessary

    // remove other action buttons in read-only view
    this.infoAboutButtons = [];
    this.infoButtons = [
      {
        type: ChatInfoButtons.PARTICIPANTS,
        imgSrc: 'assets/images/contacts.svg'
      }
    ];
    this.infoActions = [];
  }

  async testListDb() {
    let result = await this.conversationListService.chatService.getUserConversations({
      userId: this.conversationListService.loggedInUserId,
      filter: 'stream eq true',
      expand: 'conversation',
      top: 30
    });
    let userConversations = getItemsFromCollection(result.body);
    this.miChat.handleDbUserConversationList(userConversations);
  }

  setUpSidebarShowHide() {
    var allContainer = document.getElementById('chatAndSidebar');
    if (!allContainer) {
      return;
    }
    var listener = SwipeListener(allContainer);
    allContainer.addEventListener('swipe', e => {
      var directions = (<CustomEvent>e).detail.directions;

      if (document.getElementById('sidebar') && directions.left) {
        document.getElementById('sidebar').style.left = '-230px';
      }

      if (document.getElementById('sidebar') && directions.right) {
        document.getElementById('sidebar').style.left = '0';
      }
    });

    allContainer.addEventListener('click', e => {
      const target = e.target as Node;
      if (
        document.getElementById('conversationComponent') &&
        document.getElementById('sidebar') &&
        document.getElementById('conversationComponent').contains(target)
      ) {
        document.getElementById('sidebar').style.left = '-230px';
      }
    });
  }

  toggleInfo(): void {
    this.showingInfo = !this.showingInfo;
    if (this.showingInfo) {
      const allFocusElement = [
        ...this.miChatInfo?.el.nativeElement.querySelectorAll(
          'a, button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])'
        )
      ].filter(el => !el.hasAttribute('disabled'));
      allFocusElement[0].focus();
    }
    if (this.miChat) {
      this.isMobile = this.miChat.isMobile;
    }
  }

  // "Ask AI" is a one-way action, not a toggle: every click loads the AI conversation, even if
  // it's already open (e.g. re-entering after the aiMode getter dropped it - see below). Exiting
  // aiMode happens exclusively by navigating to a regular conversation from the sidebar (still
  // visible during aiMode), which flips the aiMode getter off on its own since it's derived from
  // conversationId - nothing here needs to remember or restore a "previous" conversation.
  //
  // Navigates rather than just assigning conversationId directly, matching how every other
  // conversation switch works here (conversation-list's items use routerLink) - otherwise the
  // URL stays on whatever regular conversation was open and never reflects aiMode being on.
  // ChatComponent's aiMode bootstrap requires a truthy conversationId (it's just a local key for
  // tagging/filtering messages here, not a real CloudLink conversation).
  openAiMode(): void {
    this.router.navigate(['/messages', this.AI_MODE_CONVERSATION_ID], { queryParamsHandling: 'preserve' });
  }

  onAiThreadReady(sessionId: string): void {
    console.log('AI SESSION READY', sessionId);
  }

  onAiSourcesReceived(event: { messageId: string; sources: { title: string; chunk: string }[] }): void {
    console.log('AI SOURCES RECEIVED', event);
  }

  onAiStreamError(event: { messageId: string; error: string }): void {
    console.log('AI STREAM ERROR', event);
  }

  onAiHandoffRequested(event: { conversationId: string; sessionId: string }): void {
    console.log('AI HANDOFF REQUESTED', event);
    if (this.aiConfigService.isEmbeddedInFrame) {
      this.aiConfigService.notifyParent({ type: HANDOFF_MESSAGE, ...event });
      return;
    }
    // No iframe parent to hand the conversation to, so the demo just confirms the event arrived -
    // a real host would start its live-agent, callback or transfer flow here instead.
    this.notificationService.showInfoToast(
      `Handoff requested for session ${event.sessionId}. Connect onAiHandoffRequested to your live-agent flow.`
    );
  }

  // From the sidebar's New chat button, or a cl-chat:new-chat message from an iframe parent.
  // startNewAiSession() declines (returns false) until aiMode has finished its first load.
  newAiChat(): void {
    if (!this.aiMode) {
      return;
    }
    this.miChat?.startNewAiSession();
  }

  headerButtonClick($event) {
    console.log('HEADER CLICK', $event);
    if ($event === 'info') {
      this.toggleInfo();
    }
  }

  loadOfflineConversation() {
    this.router.navigate(['/messages', this.offlineConversationId], { queryParamsHandling: 'preserve' });
  }

  onActionCardOpenLink($event) {
    console.log('ACTION CARD LINK CLICKED', $event);
    //Can preventDefault to prevent default window from opening
    //$event.preventDefault();
  }

  onActionCardEvent($event) {
    console.log('ACTION CARD EVENT', $event);
  }

  onChatInfoAction($event) {
    console.log('CHAT INFO ACTION', $event);
  }

  onInfoAboutButtonClick($event) {
    console.log('CHAT ABOUT INFO BUTTON CLICKED', $event);
  }

  uploadStart($event) {
    console.log('UPLOAD START', $event);
  }

  uploadEnd($event) {
    console.log('UPLOAD END', $event);
  }

  sendOverride(message) {
    return message;
  }

  conversationLoadStart($event) {
    console.log('LOAD START', $event);
  }

  conversationLoadEnd($event) {
    console.log('LOAD END', $event);
  }

  nameClicked(userId) {
    console.log('NAME CLICKED', userId);
  }

  mentionClicked(userId) {
    console.log('MENTION CLICKED', userId);
  }

  participantClicked(userId) {
    console.log('PARTICIPANT CLICKED', userId);
  }

  shareLocation($event) {
    console.log('SHARE LOCATION', $event);
  }

  callClicked(number) {
    console.log('CALL NUMBER CLICKED', number);
  }

  emailClicked(email) {
    console.log('EMAIL CLICKED', email);
  }

  preferencesClicked() {
    console.log('PREFERENCES CLICKED');
  }

  // Mirrors how a webview host forwards library logs to the console; only active when the library itself is not writing to it
  onChatLog(logEvent: ChatLogEvent) {
    if (this.config?.consoleOutput !== false) {
      return;
    }
    const write = logEvent.level === 'error' ? console.error : logEvent.level === 'warn' ? console.warn : console.info;
    write('[host]', logEvent.logMessage, ...(logEvent.logParameters ?? []));
  }

  analyticEvent(analyticObject: AnalyticObject) {
    console.log('ANALYTIC EVENT', analyticObject);
  }

  optInOrOutClick() {
    if (this.notificationSettings.optedInStreams.indexOf(this.conversationId) > -1) {
      // Create new object reference for signal reactivity
      this.notificationSettings = {
        ...this.notificationSettings,
        optedInStreams: this.notificationSettings.optedInStreams.filter(value => value != this.conversationId)
      };
    } else if (this.notificationSettings.optedInStreams.length < this.chatInfoConfig.maxNotificationSettings) {
      // Create new object reference for signal reactivity
      this.notificationSettings = {
        ...this.notificationSettings,
        optedInStreams: [...this.notificationSettings.optedInStreams, this.conversationId]
      };
    }
  }

  unmuteDMClick() {
    console.log('UNMUTE DIRECT CONVERSATION', this.conversationId);
    let foundDM = this.notificationSettings.mutedDirects.find(
      ({ conversationId }) => conversationId === this.conversationId
    );
    if (foundDM) {
      // Create new object reference for signal reactivity
      this.notificationSettings = {
        ...this.notificationSettings,
        mutedDirects: this.notificationSettings.mutedDirects.filter(
          ({ conversationId }) => conversationId != this.conversationId
        )
      };
    }
  }

  muteDMClick(mutedDirect: MutedDirect) {
    console.log(`MUTE DIRECT CONVERSATION ${mutedDirect.conversationId} until ${mutedDirect.muteUntil}`);
    let foundDM = this.notificationSettings.mutedDirects.find(
      ({ conversationId }) => conversationId === this.conversationId
    );
    if (foundDM) {
      // Create new object reference for signal reactivity
      const index = this.notificationSettings.mutedDirects.findIndex(el => el.conversationId === this.conversationId);
      const newMutedDirects = [...this.notificationSettings.mutedDirects];
      newMutedDirects[index] = {
        conversationId: mutedDirect.conversationId,
        muteUntil: mutedDirect.muteUntil
      };
      this.notificationSettings = {
        ...this.notificationSettings,
        mutedDirects: newMutedDirects
      };
    } else if (this.notificationSettings.mutedDirects.length < this.chatInfoConfig.maxNotificationSettings) {
      // Create new object reference for signal reactivity
      this.notificationSettings = {
        ...this.notificationSettings,
        mutedDirects: [...this.notificationSettings.mutedDirects, mutedDirect]
      };
    }
  }

  reloadSettingsClick() {
    console.log('RELOAD SETTINGS CLICKED');
  }

  editStream(conversationId) {
    console.log('EDIT STREAM ' + conversationId);
  }

  goToAttachmentMessage(attachmentId: string) {
    console.log('FOCUS ON ATTACHMENT ' + attachmentId);
    this.miChat.findAttachment(attachmentId);
  }
  onSelectMessageMode(event) {
    this.config.headerButtons[2].disabled = event;
  }

  fullScreenPreview($event) {
    console.log(`Show ${$event.type} file full screen ${$event.attachmentId}`);
    this.miChat.showFileFullScreen($event.attachmentId, $event.type);
  }
}
